import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Compass, ArrowRight } from "lucide-react";

const sections = [
  { href: "#top", label: "Top" },
  { href: "#problem", label: "The Problem" },
  { href: "#features", label: "Features" },
  { href: "#how", label: "How It Works" },
  { href: "#app", label: "Inside the App" },
  { href: "#download", label: "Early Access" },
  { href: "#why", label: "Why Farmers Use It" },
  { href: "#faq", label: "FAQ" },
];

export default function MobileQuickNav() {
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState(false);
  const [active, setActive] = useState("#top");

  // Show pill only after scrolling past hero
  useEffect(() => {
    const onScroll = () => {
      setShow(window.scrollY > 400);
      // detect active section
      const ids = sections.map((s) => s.href.replace("#", ""));
      const fromTop = window.scrollY + 120;
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= fromTop) current = id;
      }
      setActive(`#${current}`);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    // Allow sheet close animation, then scroll
    setTimeout(() => {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
  };

  const activeLabel =
    sections.find((s) => s.href === active)?.label || "Browse";

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="mobile-quicknav-trigger"
          aria-label="Jump to section"
          className={`md:hidden fixed bottom-5 left-1/2 -translate-x-1/2 z-40 inline-flex items-center gap-2 rounded-full bg-[#1F3617] text-white pl-4 pr-5 py-3 shadow-lg shadow-black/20 border border-white/10 transition-all duration-300 ${
            show
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-6 pointer-events-none"
          }`}
        >
          <Compass className="w-4 h-4 text-[#F2C76A]" />
          <span className="text-sm font-bold tracking-tight">
            {activeLabel}
          </span>
          <span className="text-xs text-white/60 font-semibold">▾</span>
        </button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        className="md:hidden bg-[#1F3617] border-t border-white/10 text-white rounded-t-2xl px-5 pt-6 pb-8"
        data-testid="mobile-quicknav-sheet"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-white text-lg">
            Jump to
          </SheetTitle>
        </SheetHeader>

        <ul className="mt-5 space-y-1">
          {sections.map((s) => {
            const isActive = s.href === active;
            return (
              <li key={s.href}>
                <button
                  type="button"
                  onClick={() => go(s.href)}
                  data-testid={`quicknav-${s.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`w-full flex items-center justify-between text-left py-3.5 px-4 rounded-md transition-colors border ${
                    isActive
                      ? "bg-[#F2C76A]/15 border-[#F2C76A]/40 text-[#F2C76A]"
                      : "border-transparent hover:bg-white/5 text-white/90"
                  }`}
                >
                  <span className="font-semibold text-base">{s.label}</span>
                  <ArrowRight
                    className={`w-4 h-4 ${isActive ? "text-[#F2C76A]" : "text-white/40"}`}
                  />
                </button>
              </li>
            );
          })}
        </ul>

        <Button
          asChild
          data-testid="quicknav-cta"
          className="mt-5 w-full bg-[#D96C32] hover:bg-[#B85A28] text-white font-bold py-4 h-auto rounded-md"
        >
          <a href="#cta" onClick={() => go("#cta")}>
            Request a Demo
            <ArrowRight className="ml-2 w-5 h-5" />
          </a>
        </Button>
      </SheetContent>
    </Sheet>
  );
}
